// "Obvious default" leader detection for the ranker's in-tier tie-breaker.
//
// When the caret sits inside an alias binding's definition span at a relation
// slot — re-editing `INNER JOIN ▮staff s ON s.staff_id = p.staff_id` — the
// relation already written there is the best suggestion by construction: the
// rest of the statement was authored against it. The resolvers' caret-binding
// promotion (fkJoin/caretBindingPromotion.ts) already lifts that TABLE into
// tier 1, but the tier is shared with every FK-join snippet the ranker
// promotes, and alphabetical order then buries `staff` under `address ON …`.
// This tie-breaker floats the caret binding's relation — bare table/view
// candidate AND the FK-join snippet(s) for the same relation — to the top of
// its tier. Engine-agnostic: reads only alias bindings + the caret offset.

import type { AliasBinding, CompletionCandidate, CompletionContext } from '@/core/engines';

const stripQuotes = (s: string): string => s.replace(/[[\]"`]/g, '');

/** Lower-cased bare relation of the table/view/CTE binding whose definition
 *  span contains the caret, or undefined when the caret is elsewhere. A CTE
 *  used as a DML target (`UPDATE ▮cheap`) is bound with `kind: 'cte'`. */
export function caretBindingRelation(context: CompletionContext): string | undefined {
  const binding = context.aliasBindings.find((b) => isCaretInside(b, context.offset));
  return binding ? stripQuotes(binding.relation).toLowerCase() : undefined;
}

function isCaretInside(b: AliasBinding, offset: number): boolean {
  return (
    (b.kind === 'table' || b.kind === 'view' || b.kind === 'cte') &&
    b.definedAt.start <= offset &&
    offset <= b.definedAt.end
  );
}

/**
 * True when `candidate` names the caret binding's relation: a table/view/CTE
 * whose bare (or schema-qualified) label matches, or an FK-join snippet whose
 * `filterText` (the bare relation name — see fkJoinSnippet.ts) matches.
 */
export function isCaretBindingLeader(
  candidate: CompletionCandidate,
  bareName: string,
  caretRelation: string | undefined,
): boolean {
  if (!caretRelation) return false;
  if (candidate.kind === 'table' || candidate.kind === 'view' || candidate.kind === 'cte') {
    const l = stripQuotes(bareName).toLowerCase();
    return l === caretRelation || l.endsWith(`.${caretRelation}`);
  }
  if (candidate.fkJoin && candidate.filterText) {
    const l = stripQuotes(candidate.filterText).toLowerCase();
    return l === caretRelation || l.endsWith(`.${caretRelation}`);
  }
  return false;
}
